import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { isMSP } from "../../helpers/util_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import {
  getCustomerRules,
  clearBillingManagementState,
  updateRule,
  updateRules,
  clearBillingManagementMessages,
} from "../../store/billingManagement/billingManagementSlice";
import { getCustomerMetadata as getCustomerMetadataApi } from "../../helpers/auth_aws_helper";
import EditBillingRuleModal from "./EditBillingRuleModal";
import { useBillingCustomerOrgDisplay } from "./useBillingCustomerOrgDisplay";

/** Short label on chip, full label in tooltip (known billing frequency enums). */
function getFrequencyChipMeta(frequency) {
  if (frequency == null || frequency === "") return { short: "—", full: "" };
  const key = String(frequency).toUpperCase().replace(/\s+/g, "_");
  if (key === "RECURRING") return { short: "R", full: "Recurring" };
  if (key === "ONE_TIME") return { short: "NR", full: "Non Recurring" };
  if (key === "ON_DEMAND" || key === "ONDEMAND") return { short: "NR", full: "Non Recurring" };
  if (key === "SCHEDULED") return { short: "Sch", full: "Scheduled" };
  const full = String(frequency)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
  const short =
    full.length <= 4
      ? full
      : full
          .split(" ")
          .map((w) => w.charAt(0))
          .join("")
          .slice(0, 3);
  return { short: short || "—", full: full || String(frequency) };
}

const CustomerBillingRules = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { customerOrgCode } = useParams();

  // Check if user is MSP
  const isMSPUser = isMSP();

  // Redux state
  const {
    customerRules,
    isFetchingCustomerRules,
    fetchCustomerRulesError,
    pagination,
    nextInvoiceDate,
    createRulesSuccess,
    updateRuleSuccess,
    updateRuleError,
    updateRulesSuccess,
    updateRulesError,
    isUpdatingRule,
    isUpdatingRules,
    updatingRuleId,
  } = useSelector((state) => state.billingManagement);

  const { displayName: customerOrgDisplayName } = useBillingCustomerOrgDisplay(customerOrgCode, {
    enabled: isMSPUser,
  });

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState("");
  const [accountsModalOpen, setAccountsModalOpen] = useState(false);
  const [selectedRuleAccounts, setSelectedRuleAccounts] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteRuleTarget, setDeleteRuleTarget] = useState(null);
  const [selectedRuleIds, setSelectedRuleIds] = useState([]);
  const [bulkConfirm, setBulkConfirm] = useState({ open: false, action: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Setup readiness (kind=setup_readiness) – show when ready is false
  const [setupReadiness, setSetupReadiness] = useState(null);
  const [isFetchingSetupReadiness, setIsFetchingSetupReadiness] = useState(false);

  const tableContainerRef = useRef(null);
  const pendingScrollRestoreRef = useRef(null);

  const showInitialRulesLoading =
    isFetchingCustomerRules && customerRules.length === 0 && !fetchCustomerRulesError;
  const showRulesTable =
    !fetchCustomerRulesError && !(isFetchingCustomerRules && customerRules.length === 0);
  const showSilentListRefresh =
    (isFetchingCustomerRules && customerRules.length > 0) || isUpdatingRules;

  const isRowSyncing = (ruleId) =>
    Boolean(ruleId && updatingRuleId === ruleId && (isUpdatingRule || isFetchingCustomerRules));

  const tableBusy = isUpdatingRule || isUpdatingRules;

  useEffect(() => {
    setSelectedRuleIds([]);
  }, [customerOrgCode, page, statusFilter, categoryFilter]);

  // Redirect if not MSP user
  useEffect(() => {
    if (!isMSPUser) {
      history.push("/dashboard");
      return;
    }
  }, [isMSPUser, history]);

  // Redirect if no customerOrgCode
  useEffect(() => {
    if (!customerOrgCode) {
      history.push("/billing-management");
      return;
    }
  }, [customerOrgCode, history]);

  // Fetch customer rules on mount
  useEffect(() => {
    if (isMSPUser && customerOrgCode) {
      dispatch(
        getCustomerRules({
          customerOrgCode,
          params: { page, page_size: pageSize, status: statusFilter || undefined },
        })
      );
    }

    return () => {};
  }, [dispatch, isMSPUser, customerOrgCode, page, pageSize, statusFilter]);

  // Fetch setup readiness (kind=setup_readiness) to show missing setup steps when ready is false
  useEffect(() => {
    if (!customerOrgCode || !isMSPUser) {
      setSetupReadiness(null);
      return;
    }
    setIsFetchingSetupReadiness(true);
    getCustomerMetadataApi(customerOrgCode, { kind: "setup_readiness" })
      .then((res) => {
        const data = res?.data ?? res;
        setSetupReadiness({
          ready: data?.ready === true,
          effective_billing_mode: data?.effective_billing_mode,
          checklist: Array.isArray(data?.checklist) ? data.checklist : [],
        });
      })
      .catch(() => setSetupReadiness(null))
      .finally(() => setIsFetchingSetupReadiness(false));
  }, [customerOrgCode, isMSPUser]);

  // Refresh rules list when a rule is successfully created
  useEffect(() => {
    if (createRulesSuccess && isMSPUser && customerOrgCode) {
      dispatch(
        getCustomerRules({
          customerOrgCode,
          params: { page, page_size: pageSize, status: statusFilter || undefined },
        })
      );
    }
  }, [createRulesSuccess, dispatch, isMSPUser, customerOrgCode, page, pageSize, statusFilter]);

  // Format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return "--";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  // Format date to month year
  const formatMonthYear = (dateString) => {
    if (!dateString) return "--";
    try {
      // Handle YYYY-MM format (e.g., "2026-02")
      if (dateString.match(/^\d{4}-\d{2}$/)) {
        const [year, month] = dateString.split("-");
        const monthIndex = parseInt(month, 10) - 1; // Convert to 0-indexed
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return `${monthNames[monthIndex]} ${year}`;
      }
      // Handle full date strings (YYYY-MM-DD or ISO format)
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "--";
      }
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
      });
    } catch (error) {
      return "--";
    }
  };

  // Format full date (for next_invoice_date)
  const formatFullDate = (dateString) => {
    if (!dateString) return null;
    try {
      // Expecting YYYY-MM-DD; parse manually to avoid timezone shifts
      const parts = dateString.split("-");
      if (parts.length < 3) return null;
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // 0-indexed
      const day = parseInt(parts[2], 10);
      if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;

      const date = new Date(year, month, day); // local date, no TZ offset surprises
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return null;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "ACTIVE":
        return { bg: "#10b981", text: "#ffffff" };
      case "INACTIVE":
        return { bg: "#64748b", text: "#ffffff" };
      default:
        return { bg: "#e2e8f0", text: "#475569" };
    }
  };

  const getRuleTypeDisplay = (ruleType) => {
    if (!ruleType) return "--";
    return ruleType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getRuleCategoryDisplay = (category) => {
    if (!category) return "--";
    return category
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const categoryFilterOptions = useMemo(() => {
    if (!customerRules?.length) return [];
    const map = new Map();
    customerRules.forEach((r) => {
      const key = r?.rule_category;
      if (!key) return;
      if (!map.has(key)) {
        const label =
          r.category_label != null && r.category_label !== "" ? r.category_label : getRuleCategoryDisplay(key);
        map.set(key, label);
      }
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [customerRules]);

  const filteredRules = useMemo(() => {
    if (!customerRules || !Array.isArray(customerRules)) return [];
    let list = customerRules;
    if (categoryFilter) {
      list = list.filter((r) => r && r.rule_category === categoryFilter);
    }
    if (!searchTerm) return list;
    const searchLower = searchTerm.toLowerCase();
    return list.filter((rule) => {
      if (!rule) return false;
      const freqMeta = getFrequencyChipMeta(rule.frequency);
      const freqSearchHaystack = [rule.frequency, freqMeta.full, freqMeta.short].filter(Boolean).join(" ").toLowerCase();
      return (
        rule.rule_name?.toLowerCase().includes(searchLower) ||
        rule.rule_number?.toLowerCase().includes(searchLower) ||
        rule.rule_category?.toLowerCase().includes(searchLower) ||
        rule.rule_type?.toLowerCase().includes(searchLower) ||
        rule.rule_label?.toLowerCase().includes(searchLower) ||
        rule.status?.toLowerCase().includes(searchLower) ||
        freqSearchHaystack.includes(searchLower) ||
        String(rule.created_by_username || rule.username || rule.user_name || rule.created_by || "").toLowerCase().includes(searchLower)
      );
    });
  }, [customerRules, categoryFilter, searchTerm]);

  const selectedRuleIdSet = useMemo(() => new Set(selectedRuleIds), [selectedRuleIds]);
  const visibleRuleIds = useMemo(
    () => filteredRules.map((r) => r.rule_id).filter(Boolean),
    [filteredRules]
  );
  const allVisibleSelected =
    visibleRuleIds.length > 0 && visibleRuleIds.every((id) => selectedRuleIdSet.has(id));
  const someVisibleSelected = visibleRuleIds.some((id) => selectedRuleIdSet.has(id));

  const selectedRulesForBulk = useMemo(
    () =>
      selectedRuleIds.map((id) => customerRules.find((r) => r.rule_id === id)).filter(Boolean),
    [selectedRuleIds, customerRules]
  );

  const allSelectedAlreadyActive =
    selectedRulesForBulk.length > 0 &&
    selectedRulesForBulk.every((r) => String(r.status || "").toUpperCase() === "ACTIVE");
  const allSelectedAlreadyInactive =
    selectedRulesForBulk.length > 0 &&
    selectedRulesForBulk.every((r) => String(r.status || "").toUpperCase() === "INACTIVE");

  const bulkActivateDisabled = tableBusy || allSelectedAlreadyActive;
  const bulkDeactivateDisabled = tableBusy || allSelectedAlreadyInactive;

  const toggleSelectRule = (ruleId) => {
    if (!ruleId || tableBusy) return;
    setSelectedRuleIds((prev) => (prev.includes(ruleId) ? prev.filter((x) => x !== ruleId) : [...prev, ruleId]));
  };

  const toggleSelectAllVisible = () => {
    if (tableBusy) return;
    if (allVisibleSelected) {
      setSelectedRuleIds((prev) => prev.filter((id) => !visibleRuleIds.includes(id)));
    } else {
      setSelectedRuleIds((prev) => [...new Set([...prev, ...visibleRuleIds])]);
    }
  };

  // Calculate total accounts from linked_account_ids
  const getAccountCount = (rule) => {
    if (!rule.linked_account_ids) return { display: 0, isAll: false, accounts: [] };
    if (Array.isArray(rule.linked_account_ids)) {
      if (rule.linked_account_ids.includes("ALL") || rule.linked_account_ids.length === 0) {
        return { display: "All", isAll: true, accounts: [] };
      }
      return { display: rule.linked_account_ids.length, isAll: false, accounts: rule.linked_account_ids };
    }
    return { display: 0, isAll: false, accounts: [] };
  };

  // Handle account cell click
  const handleAccountClick = (rule) => {
    const accountInfo = getAccountCount(rule);
    if (!accountInfo.isAll && accountInfo.accounts.length > 0) {
      setSelectedRuleAccounts(accountInfo.accounts);
      setAccountsModalOpen(true);
    }
  };

  // Build update payload from an existing rule, with optional status override
  const buildUpdatePayloadFromRule = (rule, statusOverride) => {
    if (!rule) return null;

    // Omit LINKED_ACCOUNT scope when scope_value is ALL (API expects no scope for "all accounts")
    const rawScopes = rule.scopes || [];
    const scopes = rawScopes.filter(
      (s) => !(s.scope_type === "LINKED_ACCOUNT" && String(s.scope_value || "").toUpperCase() === "ALL")
    );

    const payload = {
      name: rule.rule_name || rule.name,
      status: statusOverride || rule.status,
      scopes,
      configuration: rule.configuration || { parameters: {} },
    };

    if (rule.description) {
      payload.description = rule.description;
    }

    // Normalize date to YYYY-MM-DD for API (same format as end_date)
    const toFullDate = (d) => {
      if (!d) return null;
      const s = String(d).trim();
      if (/^\d{4}-\d{2}$/.test(s)) return `${s}-01`;
      return s.length >= 10 ? s.substring(0, 10) : s;
    };

    if (rule.frequency === "RECURRING") {
      const startDate = rule.start_date || rule.start_month;
      const endDate = rule.end_date || rule.end_month;
      if (startDate) {
        payload.start_date = toFullDate(startDate);
      }
      if (endDate) {
        payload.end_date = toFullDate(endDate);
      } else if (!endDate && rule.frequency === "RECURRING") {
        payload.end_date = null;
      }
    }

    return payload;
  };

  // Batch PUT /rules expects `id` plus the same body shape as single-rule update
  const buildBatchItemFromRule = (rule, statusOverride) => {
    const data = buildUpdatePayloadFromRule(rule, statusOverride);
    if (!data || !rule?.rule_id) return null;
    return { id: rule.rule_id, ...data };
  };

  const executeBulkRulesAction = (action) => {
    const statusByAction = { activate: "ACTIVE", deactivate: "INACTIVE", delete: "DELETED" };
    const status = statusByAction[action];
    if (!status || !selectedRuleIds.length) return;

    const items = selectedRuleIds
      .map((id) => {
        const rule = customerRules.find((r) => r.rule_id === id);
        return rule ? buildBatchItemFromRule(rule, status) : null;
      })
      .filter(Boolean);

    if (items.length === 0) return;

    pendingScrollRestoreRef.current = {
      table: tableContainerRef.current?.scrollTop ?? 0,
      windowY: typeof window !== "undefined" ? window.scrollY : 0,
    };
    dispatch(updateRules({ customerOrgCode, rules: items }));
  };

  // Handle status toggle (no confirmation — API update only)
  const handleStatusToggle = (rule, newStatusValue) => {
    if (!rule?.rule_id) return;
    const payload = buildUpdatePayloadFromRule(rule, newStatusValue);
    if (!payload) return;
    dispatch(updateRule({ customerOrgCode, ruleId: rule.rule_id, ruleData: payload }));
  };

  // Handle delete click
  const handleDeleteClick = (rule) => {
    setDeleteRuleTarget(rule);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete (soft delete via status = DELETED)
  const handleConfirmDelete = () => {
    if (!deleteRuleTarget) return;

    const payload = buildUpdatePayloadFromRule(deleteRuleTarget, "DELETED");
    if (!payload) return;

    dispatch(updateRule({ customerOrgCode, ruleId: deleteRuleTarget.rule_id, ruleData: payload }));
    setDeleteConfirmOpen(false);
    setDeleteRuleTarget(null);
  };

  // Handle status update success/error
  useEffect(() => {
    if (updateRuleSuccess && isMSPUser && customerOrgCode) {
      pendingScrollRestoreRef.current = {
        table: tableContainerRef.current?.scrollTop ?? 0,
        windowY: typeof window !== "undefined" ? window.scrollY : 0,
      };
      setSnackbar({
        open: true,
        message: "Rule updated successfully.",
        severity: "success",
      });
      dispatch(
        getCustomerRules({
          customerOrgCode,
          params: { page, page_size: pageSize, status: statusFilter || undefined },
        })
      );
      // Defer clear so EditBillingRuleModal's effect runs first while updateRuleSuccess is still set
      // (parent effects run before child effects; sync clear prevented the modal from scheduling its close timer).
      setTimeout(() => dispatch(clearBillingManagementMessages()), 0);
    }
  }, [updateRuleSuccess, dispatch, isMSPUser, customerOrgCode, page, pageSize, statusFilter]);

  useEffect(() => {
    if (updateRulesSuccess && isMSPUser && customerOrgCode) {
      pendingScrollRestoreRef.current = {
        table: tableContainerRef.current?.scrollTop ?? 0,
        windowY: typeof window !== "undefined" ? window.scrollY : 0,
      };
      setSnackbar({
        open: true,
        message: "Rules updated successfully.",
        severity: "success",
      });
      setSelectedRuleIds([]);
      setBulkConfirm({ open: false, action: null });
      dispatch(clearBillingManagementMessages());
      dispatch(
        getCustomerRules({
          customerOrgCode,
          params: { page, page_size: pageSize, status: statusFilter || undefined },
        })
      );
    }
  }, [updateRulesSuccess, dispatch, isMSPUser, customerOrgCode, page, pageSize, statusFilter]);

  useEffect(() => {
    if (updateRulesError) {
      const message =
        typeof updateRulesError === "string"
          ? updateRulesError
          : getApiErrorMessage(updateRulesError) || "Failed to update rules.";
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
      setBulkConfirm({ open: false, action: null });
      dispatch(clearBillingManagementMessages());
    }
  }, [updateRulesError, dispatch]);

  useEffect(() => {
    if (isFetchingCustomerRules) return;
    const saved = pendingScrollRestoreRef.current;
    if (!saved) return;
    pendingScrollRestoreRef.current = null;
    const { table, windowY } = saved;
    requestAnimationFrame(() => {
      window.scrollTo({ top: windowY, left: 0, behavior: "auto" });
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = table;
      }
    });
  }, [isFetchingCustomerRules, customerRules]);

  useEffect(() => {
    if (updateRuleError) {
      const message = typeof updateRuleError === "string" ? updateRuleError : getApiErrorMessage(updateRuleError) || "Failed to update rule status.";
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
      dispatch(clearBillingManagementMessages());
    }
  }, [updateRuleError, dispatch]);

  // Get charge amount from rule_config or configuration
  const getChargeAmount = (rule) => {
    // Try to extract amount from rule_config or configuration
    if (rule.rule_config) {
      // If it's a percentage, show as percentage
      if (typeof rule.rule_config === "string" && rule.rule_config.includes("%")) {
        return rule.rule_config;
      }
      // If it's a number, format as currency
      const num = parseFloat(rule.rule_config);
      if (!isNaN(num)) {
        return formatCurrency(num);
      }
      return rule.rule_config;
    }
    if (rule.configuration?.parameters?.amount) {
      return formatCurrency(rule.configuration.parameters.amount);
    }
    if (rule.configuration?.parameters?.percent) {
      return `${rule.configuration.parameters.percent}%`;
    }
    return "--";
  };

  return (
    <Box
      sx={{
        padding: { xs: "1rem", sm: "1.5rem", md: "2rem" },
        mb: "3rem",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      {/* Breadcrumbs with Back Button */}
      <Box sx={{ mb: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Breadcrumbs
          separator={
            <Box
              component="span"
              sx={{
                color: "#cbd5e1",
                fontSize: "0.875rem",
                mx: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              /
            </Box>
          }
          aria-label="breadcrumb"
          sx={{
            "& .MuiBreadcrumbs-ol": {
              flexWrap: "nowrap",
            },
          }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push("/billing-management")}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1e293b",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Billing Management
          </Link>
          <Typography
            variant="body2"
            sx={{
              color: "#3b82f6",
              fontSize: "0.875rem",
              fontWeight: 600,
              px: 0.5,
            }}
          >
            Billing Rules{customerOrgDisplayName ? ` (${customerOrgDisplayName})` : ""}
          </Typography>
        </Breadcrumbs>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push("/billing-management")}
          sx={{
            textTransform: "none",
            color: "#64748b",
            fontSize: "0.875rem",
            fontWeight: 500,
            "&:hover": {
              backgroundColor: alpha("#3b82f6", 0.1),
              color: "#3b82f6",
            },
          }}
        >
          Back
        </Button>
      </Box>

      {/* Rules Table */}
      <Card
        sx={{
          borderRadius: "12px",
          border: "none",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
          backgroundColor: "#ffffff",
        }}
      >
        <CardContent sx={{ p: 0, position: "relative" }}>
          {showSilentListRefresh && (
            <LinearProgress
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                borderRadius: "12px 12px 0 0",
                zIndex: 2,
              }}
            />
          )}
          {/* Search and Action Bar */}
          <Box
            sx={{
              p: 1.5,
              borderBottom: "1px solid #e2e8f0",
              backgroundColor: "#ffffff",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              borderRadius: "12px 12px 0 0",
            }}
          >
            {selectedRuleIds.length > 0 && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  pb: 1.25,
                  mb: 0.25,
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <Typography variant="body2" sx={{ color: "#475569", fontWeight: 600, fontSize: "0.8125rem", mr: 0.5 }}>
                  {selectedRuleIds.length} selected
                </Typography>
                <Tooltip
                  title={allSelectedAlreadyActive ? "Every selected rule is already active." : ""}
                  disableHoverListener={!allSelectedAlreadyActive}
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={bulkActivateDisabled}
                      onClick={() => setBulkConfirm({ open: true, action: "activate" })}
                      sx={{ textTransform: "none", fontSize: "0.75rem", borderColor: "#10b981", color: "#059669" }}
                    >
                      Activate
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  title={allSelectedAlreadyInactive ? "Every selected rule is already inactive." : ""}
                  disableHoverListener={!allSelectedAlreadyInactive}
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={bulkDeactivateDisabled}
                      onClick={() => setBulkConfirm({ open: true, action: "deactivate" })}
                      sx={{ textTransform: "none", fontSize: "0.75rem", borderColor: "#94a3b8", color: "#475569" }}
                    >
                      Deactivate
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={tableBusy}
                  onClick={() => setBulkConfirm({ open: true, action: "delete" })}
                  sx={{ textTransform: "none", fontSize: "0.75rem", borderColor: "#fecaca", color: "#dc2626" }}
                >
                  Delete
                </Button>
                <Button
                  size="small"
                  variant="text"
                  disabled={tableBusy}
                  onClick={() => setSelectedRuleIds([])}
                  sx={{ textTransform: "none", fontSize: "0.75rem", color: "#64748b", ml: "auto" }}
                >
                  Clear selection
                </Button>
              </Box>
            )}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", flex: 1, minWidth: 0 }}>
              <TextField
                size="small"
                placeholder="Search rules..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  minWidth: 200,
                  maxWidth: 400,
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.8125rem",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    height: "36px",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                      backgroundColor: "#ffffff",
                    },
                    "&.Mui-focused": {
                      borderColor: "#3b82f6",
                      backgroundColor: "#ffffff",
                      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
              />
              <TextField
                select
                size="small"
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
                aria-label="Filter by category"
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => {
                    if (!value) {
                      return (
                        <Box component="span" sx={{ color: "#9ca3af" }}>
                          All categories
                        </Box>
                      );
                    }
                    const pair = categoryFilterOptions.find(([v]) => v === value);
                    return pair ? pair[1] : value;
                  },
                }}
                sx={{
                  minWidth: 200,
                  flexShrink: 0,
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.8125rem",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    height: "36px",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                      backgroundColor: "#ffffff",
                    },
                    "&.Mui-focused": {
                      borderColor: "#3b82f6",
                      backgroundColor: "#ffffff",
                      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                    },
                    "& fieldset": { border: "none" },
                  },
                }}
              >
                <MenuItem value="">
                  <em>All categories</em>
                </MenuItem>
                {categoryFilterOptions.map(([value, label]) => (
                  <MenuItem key={value} value={value} sx={{ fontSize: "0.8125rem" }}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", ml: "auto" }}>
              {formatFullDate(nextInvoiceDate) && (
                <Chip
                  label={`Next Invoice Run on ${formatFullDate(nextInvoiceDate)}`}
                  sx={{
                    backgroundColor: "#fff7ed",
                    color: "#c2410c",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    height: "24px",
                    borderRadius: "4px",
                  }}
                />
              )}
              <Button
                variant="contained"
                onClick={() => history.push(`/billing-management/customer/${customerOrgCode}/rules/create`)}
                sx={{
                  backgroundColor: "#3b82f6",
                  color: "#ffffff",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  px: 2,
                  py: 0.625,
                  height: "36px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#2563eb",
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                Create Rule
              </Button>
            </Box>
          </Box>

          {/* Setup incomplete – show when setup_readiness.ready is false */}
          {!isFetchingSetupReadiness && setupReadiness && setupReadiness.ready === false && setupReadiness.checklist.length > 0 && (() => {
            const missingItems = setupReadiness.checklist.filter((item) => String(item?.status || "").toUpperCase() === "MISSING");
            if (missingItems.length === 0) return null;
            return (
              <Alert
                severity="warning"
                icon={<WarningAmberIcon />}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  "& .MuiAlert-message": { width: "100%" },
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "#92400e", mb: 1 }}>
                  Setup incomplete
                </Typography>
                <List dense disablePadding sx={{ listStyle: "decimal", pl: 2 }}>
                  {missingItems.map((item, idx) => (
                    <ListItem key={item.key || idx} disablePadding sx={{ display: "list-item", py: 0.25 }}>
                      <ListItemText
                        primary={item.message || item.label || item.key}
                        primaryTypographyProps={{ sx: { fontSize: "0.8125rem", color: "#78350f" } }}
                      />
                    </ListItem>
                  ))}
                </List>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => history.push("/billing-management/configure-profile")}
                  sx={{ mt: 1.5, textTransform: "none", fontSize: "0.8125rem", borderColor: "#b45309", color: "#b45309", "&:hover": { borderColor: "#92400e", backgroundColor: "rgba(180, 83, 9, 0.04)" } }}
                >
                  Open Billing Profile
                </Button>
              </Alert>
            );
          })()}

          {/* Initial load only — keep table visible during background refresh */}
          {showInitialRulesLoading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 8,
              }}
            >
              <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
              <Typography
                variant="body2"
                sx={{
                  ml: 2,
                  color: "#64748b",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                Loading rules...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {fetchCustomerRulesError && !isFetchingCustomerRules && (
            <Box
              sx={{
                p: 3,
                backgroundColor: "#fef2f2",
                borderRadius: "8px",
                border: "1px solid #f87171",
                m: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#991b1b",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                {typeof fetchCustomerRulesError === "string"
                  ? fetchCustomerRulesError
                  : "An error occurred while fetching billing rules."}
              </Typography>
            </Box>
          )}

          {/* Table */}
          {showRulesTable && (
            <TableContainer
              ref={tableContainerRef}
              sx={{
                maxHeight: "calc(100vh - 400px)",
                "&::-webkit-scrollbar": {
                  width: "6px",
                  height: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f8fafc",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: "3px",
                  "&:hover": {
                    backgroundColor: "#94a3b8",
                  },
                },
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f0f4f8" }}>
                    <TableCell
                      padding="checkbox"
                      sx={{
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        backgroundColor: "#f0f4f8",
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={allVisibleSelected}
                        indeterminate={someVisibleSelected && !allVisibleSelected}
                        disabled={tableBusy || visibleRuleIds.length === 0}
                        onChange={toggleSelectAllVisible}
                        inputProps={{ "aria-label": "Select all rules on this page" }}
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#334155",
                        fontSize: "0.8125rem",
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        textTransform: "none",
                        letterSpacing: "0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Rule No.
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#334155",
                        fontSize: "0.8125rem",
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        textTransform: "none",
                        letterSpacing: "0",
                      }}
                    >
                      Rule Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#334155",
                        fontSize: "0.8125rem",
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        textTransform: "none",
                        letterSpacing: "0",
                      }}
                    >
                      Account
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#334155",
                        fontSize: "0.8125rem",
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        textTransform: "none",
                        letterSpacing: "0",
                      }}
                    >
                      Rule Category
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#334155",
                        fontSize: "0.8125rem",
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        textTransform: "none",
                        letterSpacing: "0",
                      }}
                    >
                      Rule Sub Category
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#334155",
                        fontSize: "0.8125rem",
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        textTransform: "none",
                        letterSpacing: "0",
                      }}
                    >
                      Rule Config
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: "#334155",
                        fontSize: "0.8125rem",
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        textTransform: "none",
                        letterSpacing: "0",
                        textAlign: "center",
                      }}
                    >
                      Active period
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#334155",
                        fontSize: "0.8125rem",
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        textTransform: "none",
                        letterSpacing: "0",
                      }}
                    >
                      Username
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#334155",
                        fontSize: "0.8125rem",
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        textTransform: "none",
                        letterSpacing: "0",
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#334155",
                        fontSize: "0.8125rem",
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        textTransform: "none",
                        letterSpacing: "0",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRules.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#6b7280",
                            fontSize: "0.875rem",
                          }}
                        >
                          {searchTerm
                            ? "No rules found matching your search."
                            : categoryFilter
                              ? "No rules in this category."
                              : "No billing rules found."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRules.map((rule, index) => {
                      const statusColors = getStatusColor(rule.status);
                      const rowBusy = isRowSyncing(rule.rule_id);
                      return (
                        <TableRow
                          key={rule.rule_id || index}
                          sx={{
                            "&:hover": {
                              backgroundColor: alpha("#3b82f6", 0.02),
                            },
                            borderBottom: "1px solid #f1f5f9",
                            ...(rowBusy && {
                              backgroundColor: alpha("#3b82f6", 0.04),
                              position: "relative",
                            }),
                          }}
                        >
                          <TableCell padding="checkbox" sx={{ py: 1.5 }}>
                            <Checkbox
                              size="small"
                              checked={rule.rule_id ? selectedRuleIdSet.has(rule.rule_id) : false}
                              disabled={tableBusy || !rule.rule_id}
                              onChange={() => toggleSelectRule(rule.rule_id)}
                              inputProps={{ "aria-label": `Select rule ${rule.rule_name || rule.rule_number || rule.rule_id || ""}` }}
                              sx={{ p: 0.5 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#1e293b",
                                fontSize: "0.875rem",
                                fontFamily: "monospace",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {rule.rule_number || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#1e293b",
                                fontSize: "0.875rem",
                              }}
                            >
                              {rule.rule_name || rule.name || "--"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            {(() => {
                              const accountInfo = getAccountCount(rule);
                              if (accountInfo.isAll) {
                                return (
                                  <Chip
                                    label={accountInfo.display}
                                    size="small"
                                    sx={{
                                      backgroundColor: "#f1f5f9",
                                      color: "#475569",
                                      fontSize: "0.75rem",
                                      fontWeight: 500,
                                      height: "24px",
                                      borderRadius: "4px",
                                    }}
                                  />
                                );
                              }
                              return (
                                <Chip
                                  label={`${accountInfo.display}`}
                                  size="small"
                                  onClick={() => handleAccountClick(rule)}
                                  sx={{
                                    backgroundColor: "#eff6ff",
                                    color: "#3b82f6",
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                    height: "24px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    "&:hover": {
                                      backgroundColor: "#dbeafe",
                                      color: "#2563eb",
                                    },
                                  }}
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#1e293b",
                                fontSize: "0.875rem",
                              }}
                            >
                              {rule.category_label != null && rule.category_label !== ""
                                ? rule.category_label
                                : getRuleCategoryDisplay(rule.rule_category)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#1e293b",
                                fontSize: "0.875rem",
                              }}
                            >
                              {rule.rule_label != null && rule.rule_label !== ""
                                ? rule.rule_label
                                : getRuleTypeDisplay(rule.rule_type)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#1e293b",
                                fontSize: "0.875rem",
                              }}
                            >
                              {getChargeAmount(rule)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              py: 1.5,
                              verticalAlign: "middle",
                              textAlign: "center",
                            }}
                          >
                            {(() => {
                              const freqMeta = getFrequencyChipMeta(rule.frequency);
                              const startText = formatMonthYear(rule.start_date || rule.start_month);
                              const endText = formatMonthYear(rule.end_date || rule.end_month);
                              const rangeText =
                                startText === "--" && endText === "--"
                                  ? "—"
                                  : startText === "--"
                                    ? endText
                                    : endText === "--"
                                      ? `${startText} – —`
                                      : `${startText} – ${endText}`;
                              const chip = (
                                <Chip
                                  label={freqMeta.short}
                                  size="small"
                                  {...(freqMeta.full
                                    ? { "aria-label": `Frequency: ${freqMeta.full}` }
                                    : {})}
                                  sx={{
                                    backgroundColor: "#f1f5f9",
                                    color: "#475569",
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                    height: "22px",
                                    minWidth: "28px",
                                    borderRadius: "4px",
                                    flexShrink: 0,
                                  }}
                                />
                              );
                              const chipWithTip =
                                freqMeta.full ? (
                                  <Tooltip title={freqMeta.full} arrow>
                                    {chip}
                                  </Tooltip>
                                ) : (
                                  chip
                                );
                              return (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1.25,
                                    flexWrap: "wrap",
                                    rowGap: 0.5,
                                    width: "100%",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{
                                      color: "#1e293b",
                                      fontSize: "0.875rem",
                                      lineHeight: 1.3,
                                      textAlign: "center",
                                    }}
                                  >
                                    {rangeText}
                                  </Typography>
                                  {chipWithTip}
                                </Box>
                              );
                            })()}
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#1e293b",
                                fontSize: "0.875rem",
                              }}
                            >
                              {rule.created_by_username ||
                                rule.username ||
                                rule.user_name ||
                                rule.created_by ||
                                rule.updated_by ||
                                "—"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "nowrap" }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={rule.status === "ACTIVE"}
                                    onChange={(e) => {
                                      const newStatus = e.target.checked ? "ACTIVE" : "INACTIVE";
                                      handleStatusToggle(rule, newStatus);
                                    }}
                                    disabled={
                                      isUpdatingRule ||
                                      isUpdatingRules ||
                                      (isFetchingCustomerRules && updatingRuleId === rule.rule_id)
                                    }
                                    size="small"
                                    sx={{
                                      "& .MuiSwitch-switchBase.Mui-checked": {
                                        color: "#10b981",
                                      },
                                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                        backgroundColor: "#10b981",
                                      },
                                    }}
                                  />
                                }
                                label={
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.75rem",
                                      fontWeight: 500,
                                      color: rule.status === "ACTIVE" ? "#10b981" : "#ef4444",
                                      ml: 0.5,
                                    }}
                                  >
                                    {rule.status === "ACTIVE" ? "Active" : "Inactive"}
                                  </Typography>
                                }
                                sx={{ m: 0 }}
                              />
                              {rowBusy && (
                                <CircularProgress size={18} thickness={5} sx={{ color: "#3b82f6", flexShrink: 0 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 1.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Tooltip title="Edit Rule">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedRule(rule);
                                  setEditModalOpen(true);
                                }}
                                disabled={isUpdatingRule || isUpdatingRules || rowBusy}
                                sx={{
                                  color: "#3b82f6",
                                  "&:hover": {
                                    backgroundColor: alpha("#3b82f6", 0.1),
                                  },
                                }}
                              >
                                <EditIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Rule">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(rule)}
                                disabled={isUpdatingRule || isUpdatingRules || rowBusy}
                                sx={{
                                  color: "#ef4444",
                                  "&:hover": {
                                    backgroundColor: alpha("#ef4444", 0.1),
                                  },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Footer with pagination info */}
          {showRulesTable && filteredRules.length > 0 && (
            <Box
              sx={{
                p: 1.5,
                borderTop: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderRadius: "0 0 12px 12px",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#64748b",
                  fontSize: "0.8125rem",
                  fontWeight: 400,
                }}
              >
                Showing {filteredRules.length} of {pagination.total || filteredRules.length} rules
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8125rem",
                    borderColor: "#e2e8f0",
                    color: "#475569",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                      backgroundColor: "#f8fafc",
                    },
                    "&.Mui-disabled": {
                      borderColor: "#e2e8f0",
                      color: "#cbd5e1",
                    },
                  }}
                >
                  Previous
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={filteredRules.length < pageSize || (pagination.total && page * pageSize >= pagination.total)}
                  onClick={() => setPage(page + 1)}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8125rem",
                    borderColor: "#e2e8f0",
                    color: "#475569",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                      backgroundColor: "#f8fafc",
                    },
                    "&.Mui-disabled": {
                      borderColor: "#e2e8f0",
                      color: "#cbd5e1",
                    },
                  }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Accounts Modal */}
      <Dialog
        open={accountsModalOpen}
        onClose={() => setAccountsModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "1.125rem",
            fontWeight: 600,
            color: "#1e293b",
            pb: 1.5,
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          Linked Accounts ({selectedRuleAccounts.length})
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: "#334155",
                      fontSize: "0.8125rem",
                      py: 1.5,
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    Account Number
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedRuleAccounts.map((accountId, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:hover": {
                        backgroundColor: alpha("#3b82f6", 0.02),
                      },
                      borderBottom: index < selectedRuleAccounts.length - 1 ? "1px solid #f1f5f9" : "none",
                    }}
                  >
                    <TableCell
                      sx={{
                        py: 1.5,
                        fontSize: "0.875rem",
                        color: "#1e293b",
                        fontFamily: "monospace",
                      }}
                    >
                      {accountId}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1.5, borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={() => setAccountsModalOpen(false)}
            sx={{
              textTransform: "none",
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: alpha("#3b82f6", 0.1),
                color: "#3b82f6",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Billing Rule Modal */}
      <EditBillingRuleModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedRule(null);
        }}
        rule={selectedRule}
        customerOrgCode={customerOrgCode}
      />

      {/* Bulk actions confirmation */}
      <Dialog
        open={bulkConfirm.open}
        onClose={() => {
          if (!isUpdatingRules) {
            setBulkConfirm({ open: false, action: null });
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "1.125rem",
            fontWeight: 600,
            color: "#1e293b",
            pb: 1.5,
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {bulkConfirm.action === "delete" && <WarningAmberIcon sx={{ color: "#d97706", fontSize: 26 }} />}
          {bulkConfirm.action === "activate" && "Activate selected rules?"}
          {bulkConfirm.action === "deactivate" && "Deactivate selected rules?"}
          {bulkConfirm.action === "delete" && "Delete selected rules?"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#475569", lineHeight: 1.6 }}>
            {bulkConfirm.action === "activate" && (
              <>
                Turn on <strong>{selectedRuleIds.length}</strong> billing rule
                {selectedRuleIds.length === 1 ? "" : "s"}? They will be set to{" "}
                <strong style={{ color: "#059669" }}>Active</strong>.
              </>
            )}
            {bulkConfirm.action === "deactivate" && (
              <>
                Turn off <strong>{selectedRuleIds.length}</strong> billing rule
                {selectedRuleIds.length === 1 ? "" : "s"}? They will be set to <strong>Inactive</strong> and will not
                apply until you activate them again.
              </>
            )}
            {bulkConfirm.action === "delete" && (
              <>
                Delete <strong>{selectedRuleIds.length}</strong> billing rule
                {selectedRuleIds.length === 1 ? "" : "s"}? They will be removed from this list and will no longer be
                available for billing.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1.5, borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={() => {
              if (!isUpdatingRules) {
                setBulkConfirm({ open: false, action: null });
              }
            }}
            disabled={isUpdatingRules}
            sx={{
              textTransform: "none",
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => executeBulkRulesAction(bulkConfirm.action)}
            disabled={isUpdatingRules || !bulkConfirm.action}
            variant="contained"
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              ...(bulkConfirm.action === "delete"
                ? {
                    backgroundColor: "#ef4444",
                    color: "#ffffff",
                    "&:hover": { backgroundColor: "#dc2626" },
                    "&.Mui-disabled": { backgroundColor: "#fecaca", color: "#ffffff" },
                  }
                : {
                    backgroundColor: "#3b82f6",
                    color: "#ffffff",
                    "&:hover": { backgroundColor: "#2563eb" },
                  }),
            }}
          >
            {isUpdatingRules
              ? "Updating..."
              : bulkConfirm.action === "activate"
                ? "Activate"
                : bulkConfirm.action === "deactivate"
                  ? "Deactivate"
                  : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Rule Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          if (!isUpdatingRule && !isUpdatingRules) {
            setDeleteConfirmOpen(false);
            setDeleteRuleTarget(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "1.125rem",
            fontWeight: 600,
            color: "#1e293b",
            pb: 1.5,
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          Delete Billing Rule
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.875rem",
              color: "#475569",
              lineHeight: 1.6,
            }}
          >
            Are you sure you want to permanently delete the billing rule{" "}
            <strong>"{deleteRuleTarget?.rule_name || deleteRuleTarget?.name || "Unknown"}"</strong>? This action
            will mark the rule as{" "}
            <strong style={{ color: "#ef4444" }}>
              DELETED
            </strong>{" "}
            and it will no longer appear in the list.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1.5, borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setDeleteRuleTarget(null);
            }}
            disabled={isUpdatingRule || isUpdatingRules}
            sx={{
              textTransform: "none",
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={isUpdatingRule || isUpdatingRules}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: "#ef4444",
              color: "#ffffff",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#dc2626",
              },
              "&.Mui-disabled": {
                backgroundColor: "#fecaca",
                color: "#ffffff",
              },
            }}
          >
            {isUpdatingRule || isUpdatingRules ? "Deleting..." : "Delete Rule"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default CustomerBillingRules;
